import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Commission Policies — referral commission rule engine (`/commission-policies`).
 * Policies can be global or affiliate-scoped, scoped by product/product-group, and
 * optionally audience-filtered by `referrer_type` + `department_ids` (employee targeting).
 * Field names and endpoints lifted verbatim from the legacy module; audience contract
 * fields (`referrer_type`, `department_ids`, `update_audience`) are the new merged contract.
 */

/* ── Enums ─────────────────────────────────────────────────────────────── */

export type CommissionPolicyScope = 'default' | 'product_group' | 'product';

/**
 * Audience filter on a commission policy. `null`/omitted = policy applies to everyone.
 * `employee` + `department_ids` = restrict to employees in those departments.
 */
export type CommissionPolicyReferrerType =
  | 'partner'
  | 'affiliate'
  | 'employee'
  | 'customer'
  | 'other'
  | 'reseller';

/* ── Types ─────────────────────────────────────────────────────────────── */

export interface CommissionPolicy {
  policy_id: number;
  affiliate_id?: number;
  affiliate_ids?: number[];
  /** A policy with no assignments applies to nobody, not to everyone. */
  is_global: boolean;
  owner_type?: 'global' | 'affiliate';
  affiliate_name?: string;
  affiliate_email?: string;
  referral_code?: string;
  affiliate_names?: string[];
  affiliate_emails?: string[];
  referral_codes?: string[];
  scope: CommissionPolicyScope;
  product_group_id?: number;
  product_group_name?: string;
  product_id?: number;
  product_name?: string;
  commission_percent: number;
  policy_name: string;
  description?: string;
  effective_date: string;
  expired_date?: string;
  priority: number;
  is_active: boolean;
  /** Audience filter — null/empty = applies to everyone. */
  referrer_type?: CommissionPolicyReferrerType | null;
  /** Only meaningful when `referrer_type === 'employee'`. */
  department_ids?: number[] | null;
  created_at: string;
  updated_at: string;
}

export interface CommissionCalculationInput {
  referrer_id: number;
  customer_id: number;
  invoice_id: number;
  service_id?: number;
  product_id?: number;
  product_group_id?: number;
  base_price: number;
  quarterly_discount?: number;
  marketing_discount?: number;
  promo_ids?: number[];
  click_id?: number;
}

export type PolicyMatchReason =
  | 'product'
  | 'product_group'
  | 'affiliate_default'
  | 'global_default'
  | 'affiliate_fallback';

export interface CommissionCalculationResult {
  base_price: number;
  quarterly_discount: number;
  marketing_discount: number;
  total_discount: number;
  amount_before_vat: number;
  commission_percent: number;
  gross_commission: number;
  tax_rate: number;
  tax_amount: number;
  net_commission: number;
  matched_policy_id?: number;
  match_reason: PolicyMatchReason;
  applied_promo_ids?: number[];
  calculated_at: string;
}

/* ── Params ────────────────────────────────────────────────────────────── */

/** Query params for `GET /commission-policies`. Uses limit/offset (not page/page_size). */
export interface CommissionPolicyListParams {
  q?: string;
  scope?: CommissionPolicyScope;
  is_active?: boolean;
  owner_type?: 'global' | 'affiliate' | 'all';
  limit?: number;
  offset?: number;
  /** Filter to policies assigned to a specific affiliate. */
  affiliate_id?: number;
}

/** `POST /commission-policies` payload. */
export interface CreateCommissionPolicyPayload {
  affiliate_id?: number;
  affiliate_ids?: number[];
  is_global?: boolean;
  scope: CommissionPolicyScope;
  product_group_id?: number;
  product_id?: number;
  commission_percent: number;
  policy_name: string;
  description?: string;
  effective_date?: string;
  expired_date?: string;
  priority?: number;
  referrer_type?: CommissionPolicyReferrerType | null;
  department_ids?: number[] | null;
}

/**
 * `PUT /commission-policies/:id` payload.
 * `update_audience: true` must be set to intentionally modify/clear `referrer_type`
 * or `department_ids`; omitting it leaves the audience unchanged.
 */
export interface UpdateCommissionPolicyPayload {
  affiliate_ids?: number[];
  is_global?: boolean;
  scope?: CommissionPolicyScope;
  product_group_id?: number;
  product_id?: number;
  policy_name?: string;
  description?: string;
  commission_percent?: number;
  effective_date?: string;
  expired_date?: string;
  priority?: number;
  is_active?: boolean;
  update_audience?: boolean;
  referrer_type?: CommissionPolicyReferrerType | null;
  department_ids?: number[] | null;
}

/* ── Helpers ────────────────────────────────────────────────────────────── */

function normalizeResults<T>(data: ResponseArray<T> & { Results?: T[] }): ResponseArray<T> {
  if (data?.Results && !data?.results) {
    (data as ResponseArray<T>).results = data.Results;
  }
  return data;
}

/* ── Query keys ────────────────────────────────────────────────────────── */

export const commissionPolicyKeys = {
  all: ['commission-policies'] as const,
  list: (params: CommissionPolicyListParams) => ['commission-policies', 'list', params] as const,
  detail: (id: number) => ['commission-policies', 'detail', id] as const,
  departments: () => ['departments', 'all'] as const,
};

/* ── Commission policies list ───────────────────────────────────────────── */

/** `GET /commission-policies` — paginated policy list (limit/offset). */
export function useCommissionPolicies(params: CommissionPolicyListParams = {}) {
  return useQuery({
    queryKey: commissionPolicyKeys.list(params),
    queryFn: async (): Promise<ResponseArray<CommissionPolicy>> => {
      const p: Record<string, unknown> = {
        limit: params.limit ?? 10,
        offset: params.offset ?? 0,
      };
      if (params.q) p.q = params.q;
      if (params.scope) p.scope = params.scope;
      if (params.is_active !== undefined) p.is_active = params.is_active;
      if (params.owner_type) p.owner_type = params.owner_type;
      if (params.affiliate_id) p.affiliate_id = params.affiliate_id;
      const res = await dataCentralApi.get<ResponseArray<CommissionPolicy> & { Results?: CommissionPolicy[] }>(
        `/commission-policies?${qs.stringify(p)}`,
      );
      return normalizeResults(res.data);
    },
  });
}

/* ── Commission policy detail ───────────────────────────────────────────── */

/** `GET /commission-policies/:id` — full policy record. */
export function useCommissionPolicy(id: number) {
  return useQuery({
    queryKey: commissionPolicyKeys.detail(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<CommissionPolicy>> => {
      const res = await dataCentralApi.get<ResponseObject<CommissionPolicy>>(
        `/commission-policies/${id}`,
      );
      return res.data;
    },
  });
}

/* ── Commission preview ─────────────────────────────────────────────────── */

/**
 * `POST /commission-policies/preview` — dry-run commission calculation.
 * Returns the full breakdown without creating a commission record.
 */
export function usePreviewCommission() {
  return useMutation({
    mutationFn: async (
      payload: CommissionCalculationInput,
    ): Promise<ResponseObject<CommissionCalculationResult>> => {
      const res = await dataCentralApi.post<ResponseObject<CommissionCalculationResult>>(
        '/commission-policies/preview',
        payload,
      );
      return res.data;
    },
  });
}

/* ── Mutations ──────────────────────────────────────────────────────────── */

/** `POST /commission-policies` — create a new policy. */
export function useCreateCommissionPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: CreateCommissionPolicyPayload,
    ): Promise<ResponseObject<CommissionPolicy>> => {
      const res = await dataCentralApi.post<ResponseObject<CommissionPolicy>>(
        '/commission-policies',
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionPolicyKeys.all });
    },
  });
}

/** `PUT /commission-policies/:id` — update a policy. Pass `update_audience: true` to modify audience fields. */
export function useUpdateCommissionPolicy(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: UpdateCommissionPolicyPayload,
    ): Promise<ResponseObject<CommissionPolicy>> => {
      const res = await dataCentralApi.put<ResponseObject<CommissionPolicy>>(
        `/commission-policies/${id}`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionPolicyKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: commissionPolicyKeys.all });
    },
  });
}

/** `DELETE /commission-policies/:id` — delete a policy. */
export function useDeleteCommissionPolicy(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ResponseObject<CommissionPolicy>> => {
      const res = await dataCentralApi.delete<ResponseObject<CommissionPolicy>>(
        `/commission-policies/${id}`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionPolicyKeys.all });
    },
  });
}

/* ── Option hooks ───────────────────────────────────────────────────────── */

interface Department {
  department_id: number;
  department_name: string;
  [key: string]: unknown;
}

interface SelectOption {
  value: number;
  label: string;
}

/** `GET /departments/all` — department options for employee-audience policy targeting. */
export function useDepartmentOptions() {
  return useQuery({
    queryKey: commissionPolicyKeys.departments(),
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<SelectOption[]> => {
      const res = await dataCentralApi.get<ResponseArray<Department>>('/departments/all');
      return (res.data.results ?? []).map((d) => ({
        value: d.department_id,
        label: d.department_name,
      }));
    },
  });
}

/**
 * Paginated affiliate options for the policy-owner picker (infinite scroll).
 * Fetches `referrer_type=non_employee` only — employee affiliates are managed
 * separately and should not appear in the customer-affiliate owner picker.
 */
export interface AffiliateOwnerOption {
  value: number;
  label: string;
}

export function useAffiliateOwnerOptions(query: string = '', page: number = 1, pageSize: number = 5) {
  return useQuery({
    queryKey: ['affiliates', 'owner-options', query, page, pageSize],
    staleTime: 2 * 60_000,
    queryFn: async (): Promise<{ options: AffiliateOwnerOption[]; hasMore: boolean; nextPage?: number }> => {
      const res = await dataCentralApi.get<
        { results?: AffiliateOwnerRaw[]; Results?: AffiliateOwnerRaw[]; pagination?: { page_next: number } }
      >(
        `/affiliates?${qs.stringify({
          search: query,
          limit: pageSize,
          offset: (page - 1) * pageSize,
          referrer_type: 'non_employee',
        })}`,
      );
      const raw = res.data;
      const results: AffiliateOwnerRaw[] = raw?.results ?? raw?.Results ?? [];
      const options = results.map((a) => ({
        value: a.affiliate_id,
        label: a.referrer_company
          ? `${a.referrer_name} (${a.referrer_company})`
          : a.referrer_name || a.referrer_email || `#${a.affiliate_id}`,
      }));
      const hasMore = raw?.pagination ? raw.pagination.page_next !== -1 : false;
      return { options, hasMore, nextPage: hasMore && raw?.pagination ? raw.pagination.page_next : undefined };
    },
  });
}

/** Minimal affiliate shape needed for the owner picker label. */
interface AffiliateOwnerRaw {
  affiliate_id: number;
  referrer_name?: string;
  referrer_email?: string;
  referrer_company?: string;
}
